import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from '@tanstack/react-router'
import { addDays, startOfDay } from 'date-fns'
import { useFilteredTasks } from '../hooks/useFilteredTasks'
import { TaskEmptyState } from './TaskEmptyState'
import type { TaskEmptyContext } from './TaskPageLayout'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { Button } from '@/components/ui/button'

/**
 * Inbox or Today at zero (design 33b, case 4) — the only place "All caught up"
 * is said. Today points at what's next: how much is due tomorrow.
 */
export function CaughtUpEmptyState({ view, openAddTask }: { view: 'today' | 'inbox' } & Partial<TaskEmptyContext>) {
  const { t } = useTranslation('tasks')
  const params = useParams({ strict: false })
  const workspaceSlug = params.workspaceSlug

  const dueTomorrow = useCallback((task: TaskDocument, openStatusIds: ReadonlyArray<string>) => {
    if (!task.dueDate || task.parentId || task.isArchived) return false
    const start = addDays(startOfDay(new Date()), 1).getTime()
    const end = addDays(startOfDay(new Date()), 2).getTime()
    return task.dueDate >= start && task.dueDate < end && openStatusIds.includes(task.statusId)
  }, [])
  const { tasks: tomorrow } = useFilteredTasks({ predicate: dueTomorrow, assignedToMeOnly: true })

  if (view === 'inbox') {
    return (
      <TaskEmptyState
        title={t('empty.caughtUp')}
        description={t('empty.inboxZero')}
        actions={
          openAddTask ? (
            <Button size="sm" onClick={openAddTask}>
              {t('empty.addTask')}
            </Button>
          ) : null
        }
      />
    )
  }

  return (
    <TaskEmptyState
      title={t('empty.caughtUp')}
      description={
        tomorrow.length > 0
          ? `${t('empty.nothingToday')} ${t('empty.dueTomorrow', { count: tomorrow.length })}`
          : t('empty.nothingToday')
      }
      actions={
        tomorrow.length > 0 && workspaceSlug ? (
          <Button size="sm" variant="secondary" asChild>
            <Link to="/$workspaceSlug/tasks/upcoming" params={{ workspaceSlug }}>
              {t('empty.seeTomorrow')}
            </Link>
          </Button>
        ) : null
      }
    />
  )
}
